import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  watchStateSchema,
  type ApiError,
  type WatchState,
  type WatchTargetType,
} from '@molt/shared';

import { announce } from './announce';
import { del, get, put } from './api';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from './interface-language';
import { queryKeys } from './query-keys';

export interface WatchTarget {
  readonly targetType: WatchTargetType;
  readonly targetId: string;
}

export interface WatchMutationVariables extends WatchTarget {
  readonly watching: boolean;
}

function watchPath(target: WatchTarget): string {
  const segment =
    target.targetType === 'card'
      ? 'cards'
      : target.targetType === 'list'
        ? 'lists'
        : 'boards';
  return `/${segment}/${encodeURIComponent(target.targetId)}/watch`;
}

function watchKey(target: WatchTarget) {
  return queryKeys.watches.detail(target.targetType, target.targetId);
}

export function getWatchState(target: WatchTarget): Promise<WatchState> {
  return get(watchPath(target), watchStateSchema);
}

export function putWatchState(target: WatchTarget): Promise<WatchState> {
  return put(watchPath(target), watchStateSchema);
}

export function deleteWatchState(target: WatchTarget): Promise<WatchState> {
  return del(watchPath(target), watchStateSchema);
}

export function watchStateQueryOptions(target: WatchTarget, enabled = true) {
  return queryOptions({
    queryKey: watchKey(target),
    queryFn: () => getWatchState(target),
    enabled,
  });
}

type WatchTranslator = InterfaceLanguageContextValue['t'];

function watchAnnouncementKey(targetType: WatchTargetType, watching: boolean): string {
  const action = watching ? 'watching' : 'stopped';
  return `primitives.watchToggle.announce.${action}.${targetType}`;
}

function watchUpdateFailedMessage(
  t: WatchTranslator,
  targetType: WatchTargetType,
): string {
  return t(`primitives.watchToggle.announce.updateFailed.${targetType}`);
}

export function useSetWatchState(): UseMutationResult<
  WatchState,
  ApiError,
  WatchMutationVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation<WatchState, ApiError, WatchMutationVariables>({
    mutationFn: ({ watching, ...target }) =>
      watching ? putWatchState(target) : deleteWatchState(target),
    onSuccess: (state) => {
      queryClient.setQueryData(
        queryKeys.watches.detail(state.targetType, state.targetId),
        state,
      );
      announce(t(watchAnnouncementKey(state.targetType, state.watching)));
    },
    onError: (error, variables) => {
      announce(error.message || watchUpdateFailedMessage(t, variables.targetType));
    },
  });
}
